import { computed, effect, inject, linkedSignal, Signal, untracked } from '@angular/core'
import { toSignal } from '@angular/core/rxjs-interop'
import { ActivatedRoute, Params, Router } from '@angular/router'
import { map } from 'rxjs'

/**
 * Reiter, die ihren Stand in einem Query-Parameter tragen (`?tab=datev-export`), adressiert per
 * **Name**, nicht per Position.
 *
 * <h3>Warum Namen</h3>
 *
 * Bis 2026-09-25 stand in der URL die Position im `mat-tab-group`. Jeder neue oder bedingte Reiter
 * verschob damit alle Deep-Links dahinter: in verschickten Mails, Lesezeichen, gespeicherten
 * Assistent-Routen und rund 60 Links quer durch die App. Beim Monitoring hing die Position sogar vom
 * Haus ab (Filialabholung/Fertigung sind bedingt). Ein Name bleibt stehen, egal was davor dazukommt.
 *
 * <h3>Alte Zahlen</h3>
 *
 * Bis 2026-10-12 wurde eine Zahl im Parameter noch als alte Position gelesen und beim Oeffnen in den
 * Namen umgeschrieben. Das ist entfernt: eine Zahl ist jetzt ein unbekannter Wert und landet auf dem
 * ersten Reiter. Neue Links schreiben NIE eine Zahl.
 */
export interface ReiterMitUrlOptionen<T extends string> {
  /** Der Query-Parameter, z. B. `'tab'` oder `'versorgungTab'`. */
  param: string
  /**
   * Die SICHTBAREN Reiter in Anzeigereihenfolge — muss zur Vorlage passen. Ein bedingter Reiter
   * (`@if`) wird hier ebenso bedingt aufgenommen, dann stimmen Position und Name immer ueberein.
   */
  reiter: () => readonly T[]
  /** Default `true` — ein Reiterwechsel legt keinen neuen Verlaufseintrag an. */
  replaceUrl?: boolean
  /** Parameter, die im SELBEN `navigate` mitgeschrieben werden (zwei parallele `merge`-Navigationen ueberschreiben sich). */
  weitereParams?: () => Params
  /**
   * Haengt die Reiterliste an nachgeladenen Daten (Hauseinstellungen, Konto), `false`, bis sie da
   * sind. Solange schreibt der Effekt nichts: sonst ersetzte er `?tab=fertigung` durch den ersten
   * Reiter, nur weil der gemeinte gerade noch nicht sichtbar ist.
   */
  bereit?: () => boolean
  /**
   * Der Reiter ohne (oder mit unbekanntem) Parameter, solange er sichtbar ist. Vorgabe ist der erste
   * sichtbare. Noetig, sobald ein bedingter Reiter VORN steht, die Seite ohne `?tab=` aber weiter
   * auf ihrem alten Standard oeffnen soll (Abrechnung: „Nächste Abrechnung" vorn nur fuer SaniUp,
   * Mail-Links ohne Parameter landen weiter auf den Einreichungen).
   */
  standard?: T
}

export interface ReiterMitUrl<T extends string> {
  /** Der gewaehlte Reiter — auch ein gerade nicht sichtbarer, `index` faellt dann auf 0. */
  readonly id: Signal<T>
  /** Fuer `[selectedIndex]` der `mat-tab-group`. */
  readonly index: Signal<number>
  waehle(id: T): void
  /** Fuer `(selectedIndexChange)` der `mat-tab-group`. */
  waehleIndex(index: number): void
}

/**
 * Liest einen Parameterwert als Reiter-Namen. Unbekanntes (auch eine alte Zahl), Leeres und nicht
 * sichtbare Reiter fallen auf den Standard zurueck (Vorgabe: den ersten sichtbaren) — sonst waere gar
 * kein Reiter gewaehlt und die Seite saehe leer aus.
 */
export function reiterAusParam<T extends string>(wert: string | null | undefined, reiter: readonly T[], standard?: T): T {
  if (wert && (reiter as readonly string[]).includes(wert)) {
    return wert as T
  }
  return standard && reiter.includes(standard) ? standard : reiter[0]
}

/** Name → Position fuer `mat-tab-group`; ein nicht sichtbarer Reiter ergibt 0, nie -1. */
export function reiterPosition<T extends string>(id: T, reiter: readonly T[]): number {
  return Math.max(0, reiter.indexOf(id))
}

/**
 * Verbindet eine `mat-tab-group` mit ihrem Query-Parameter. Muss im Injection-Kontext laufen
 * (Feld-Initialisierer einer Komponente).
 *
 * ```html
 * <mat-tab-group [selectedIndex]="reiter.index()" (selectedIndexChange)="reiter.waehleIndex($event)">
 * ```
 */
export function reiterMitUrl<T extends string>(optionen: ReiterMitUrlOptionen<T>): ReiterMitUrl<T> {
  const router = inject(Router)
  const route = inject(ActivatedRoute)
  const { param, reiter, replaceUrl = true, weitereParams, bereit, standard } = optionen

  const wert = toSignal(route.queryParamMap.pipe(map((p) => p.get(param))), { initialValue: route.snapshot.queryParamMap.get(param) })
  const id = linkedSignal<T>(() => reiterAusParam(wert(), reiter(), standard))
  const index = computed(() => reiterPosition(id(), reiter()))

  // Der erste Lauf schreibt nur den Stand fest, den die Seite beim Oeffnen hat (Default-Reiter statt
  // fehlendem oder unbekanntem Wert). Das ersetzt immer: ein eigener Verlaufseintrag dafuer liesse „Zurueck" scheinbar
  // nichts tun.
  let erstesSchreiben = true
  effect(() => {
    if (bereit && !bereit()) {
      return
    }
    const queryParams = { [param]: id(), ...weitereParams?.() }
    const ersetzen = replaceUrl || erstesSchreiben
    erstesSchreiben = false
    untracked(() => router.navigate([], { queryParams, queryParamsHandling: 'merge', replaceUrl: ersetzen }))
  })

  return {
    id: id.asReadonly(),
    index,
    waehle: (neu) => id.set(neu),
    waehleIndex: (position) => {
      const neu = reiter()[position]
      if (neu) id.set(neu)
    },
  }
}
